import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { App } from '@gin-betting/core';
import { createSelector, createStructuredSelector } from 'reselect';

import { SELECTOR_STUB, SidebarView } from 'app-constants';
import { BetslipModule, useAsyncModule } from 'async-modules';
import { useOnClickOutside } from 'hooks';
import ui from 'modules/ui';
import { Modules, State } from 'types';
import { formatDecimalOdd } from 'utils';

const {
    selectors: { selectOddFormat, selectSidebarView, selectIsSidebarOpen },
    actions: { closeSidebar },
} = ui;

interface Output {
    betsSize: number;
    totalOdd: ReturnType<typeof formatDecimalOdd> | null;
    betStatus: App.BetslipStatus;
    shouldHideSidebar: boolean;
    refContainer: RefObject<HTMLDivElement>;
}

const defaultOutput: Omit<Output, 'refContainer'> = {
    betsSize: 0,
    totalOdd: null,
    betStatus: App.BetslipStatus.INITIAL,
    shouldHideSidebar: true,
};

const useSidebarTogglerMob = (): Output => {
    const dispatch = useDispatch();
    const betslipModule = useAsyncModule<'betslip'>('betslip');
    const { pathname } = useLocation();

    const refContainer = useRef<HTMLDivElement>(null);
    const refs = useMemo(() => [refContainer], []);

    const selectBetslipBetInfo = useMemo(
        () =>
            betslipModule ? makePropsSelector(betslipModule) : SELECTOR_STUB,
        [betslipModule]
    );

    const selectProps = useMemo(
        () =>
            createStructuredSelector({
                betslipBetId:
                    betslipModule?.selectors.selectChosenBetslipBetId ??
                    SELECTOR_STUB,
                sidebarView: selectSidebarView,
                isSidebarOpen: selectIsSidebarOpen,
            }),
        [betslipModule]
    );

    const {
        betslipBetId,
        sidebarView,
        isSidebarOpen,
    } = useSelector((s: State) => selectProps(s, {}));

    const onClose = useCallback(() => {
        dispatch(closeSidebar());
    }, [dispatch]);

    useOnClickOutside({ refs, handler: onClose, isOpen: isSidebarOpen });

    const props = useSelector((s: State) =>
        selectBetslipBetInfo(s, { betslipBetId })
    );

    const shouldHideSidebar =
        sidebarView === SidebarView.betslip &&
        (props
            ? props.betsSize === 0 && props.betslipBetsCount === 1
            : defaultOutput.shouldHideSidebar);

    useEffect(() => {
        if (shouldHideSidebar) {
            dispatch(closeSidebar());
        }
    }, [dispatch, shouldHideSidebar]);

    useEffect(() => {
        dispatch(closeSidebar());
    }, [dispatch, pathname]);

    return {
        ...(props || defaultOutput),
        shouldHideSidebar,
        refContainer,
    };
};

const makePropsSelector = (betslipModule: Modules['betslip']['default']) => {
    const {
        selectors: { selectBetslipBetWithStatus, selectBetslipBetTotals },
    } = betslipModule;

    const selectBetslipBetWithStatusFinal: BetslipModule['default']['selectors']['selectBetslipBetWithStatus'] = selectBetslipBetWithStatus;
    const selectBetslipBetTotalsFinal: BetslipModule['default']['selectors']['selectBetslipBetTotals'] = selectBetslipBetTotals;

    const {
        selectors: { selectBetslipBetIds },
    } = betslipModule;

    return createSelector(
        selectBetslipBetWithStatusFinal,
        selectBetslipBetTotalsFinal,
        selectOddFormat,
        selectBetslipBetIds,
        (betslipBet, totals, oddFormat, betslipBetIds) => {
            if (!betslipBet) return null;

            const { totalOdd } = totals;
            const { betslipOddIds, status: betStatus } = betslipBet;

            return {
                betsSize: betslipOddIds.count(),
                totalOdd: totalOdd
                    ? formatDecimalOdd(totalOdd, oddFormat)
                    : null,
                betStatus,
                betslipBetsCount: betslipBetIds.count(),
            };
        }
    );
};

export default useSidebarTogglerMob;
